import styles from './search-filter.module.scss';
import React from "react";

export interface SearchFilterProps {
  name?: string;
  classes?: string;
  placeholder?: string;
  /** ms de espera tras el último teclazo antes de buscar (default 3000). */
  delay?: number;
  /** Callback con el término (trim). Se llama tras el debounce o al limpiar. */
  onSearch: (q: string) => void;
}

interface State {
  value: string;
  /** true mientras corre el temporizador de debounce (muestra el spinner). */
  pending: boolean;
}

/**
 * Filtro "omnisearch" sticky top. Debounce: cada teclazo cancela el temporizador
 * y sólo se dispara `onSearch` tras `delay` ms sin escribir (o al enviar/limpiar).
 * El término se manda como `q` al API (busca en cualquier campo).
 *
 * Ocupa `col-12 col-md-4` (ancho completo en móvil, 1/3 en tablet/desktop).
 * Mientras corre el timeout muestra un spinner; al terminar, el botón de limpiar.
 */
export default class SearchFilter extends React.Component<SearchFilterProps, State> {
  static jsClass = "SearchFilter";
  static defaultProps: Partial<SearchFilterProps> = {
    name: "mbSearchFilter",
    placeholder: "Buscar…",
    delay: 3000,
  };

  state: State = { value: "", pending: false };

  private timer?: ReturnType<typeof setTimeout>;

  componentWillUnmount() {
    this.clearTimer();
  }

  private clearTimer() {
    if (this.timer) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
  }

  private onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    this.setState({ value, pending: true });
    // Debounce: cada teclazo cancela el fetch pendiente.
    this.clearTimer();
    this.timer = setTimeout(() => {
      this.setState({ pending: false });
      this.props.onSearch?.(value.trim());
    }, this.props.delay);
  };

  private onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    this.clearTimer();
    this.setState({ pending: false });
    this.props.onSearch?.(this.state.value.trim());
  };

  private clear = () => {
    this.clearTimer();
    this.setState({ value: "", pending: false });
    this.props.onSearch?.("");
  };

  render(): React.ReactNode {
    const { classes, placeholder } = this.props;
    const { value, pending } = this.state;

    return (
      <div className={styles['mb-search'] + (classes ? ' ' + classes : '')}>
        <div className="container">
          <div className="row">
            <div className="col-12 col-md-4">
              <form
                className={styles['mb-search__inner']}
                onSubmit={this.onSubmit}
                role="search">
                <svg
                  className={styles['mb-search__icon']}
                  viewBox="0 0 24 24"
                  width="16"
                  height="16"
                  aria-hidden="true">
                  <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
                  <line x1="16.5" y1="16.5" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
                <input
                  className={styles['mb-search__input']}
                  type="search"
                  value={value}
                  onChange={this.onChange}
                  placeholder={placeholder}
                  aria-label={placeholder}
                  autoComplete="off"
                />
                {pending ? (
                  <span
                    className={styles['mb-search__spinner']}
                    role="status"
                    aria-label="Buscando…"
                  />
                ) : value ? (
                  <button
                    type="button"
                    className={styles['mb-search__clear']}
                    onClick={this.clear}
                    aria-label="Limpiar búsqueda"
                    title="Limpiar búsqueda">
                    ✕
                  </button>
                ) : null}
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
